import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Box,
  Typography,
  Stack
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { capitalizeFirstLetter } from '../../../utils/Utilities';
import { GetTicketTimeline } from '../../../ticketManagement/network/Ticket';
import { normalizeTimelineForUi } from '../../../ticketManagement/utils/ticketManagementUtils';
import { isoDateFormatDmyhs } from '../../../utils/formatTime';
import { APP_CONSTANTS } from '../../../URLConstants';

export default function ComplaintTicketsModal({
  modalOpen,
  setModalOpen,
  merchantTicketStats,
  selectedStatus
}) {
  const [filterType, setFilterType] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [conversations, setConversations] = useState({});

  const fetchTicketData = async (ticketId) => {
    GetTicketTimeline(ticketId)
      .then((response) => {
        const items = normalizeTimelineForUi(response);
        setConversations({ [ticketId]: items });
      })
      .catch(() => {
        setConversations({});
      });
  };

  const statusMapNumeric = {
    2: 'Open',
    3: 'Pending',
    4: 'Resolved',
    5: 'Closed',
    6: 'Waiting On Customer',
    7: 'Waiting On Third Party'
  };

  const getReadableTicketStatus = (ticket) => {
    const s = ticket?.status;
    if (typeof s === 'number' && statusMapNumeric[s]) return statusMapNumeric[s];
    if (typeof s === 'string' && s.trim()) {
      const lower = s.toLowerCase();
      const title = {
        open: 'Open',
        pending: 'Pending',
        resolved: 'Resolved',
        closed: 'Closed',
        'waiting on customer': 'Waiting On Customer',
        'waiting on third party': 'Waiting On Third Party'
      };
      return title[lower] || s;
    }
    return 'Unknown';
  };

  const statusColor = (status) => {
    const map = {
      Open: 'error',
      Pending: 'warning',
      Resolved: 'success',
      Closed: 'default',
      'Waiting On Customer': 'info',
      'Waiting On Third Party': 'secondary'
    };
    return map[status] || 'default';
  };

  const allTickets = Array.isArray(merchantTicketStats?.stats?.tickets)
    ? merchantTicketStats.stats.tickets
    : [];

  const filteredTickets = allTickets.filter((ticket) => {
    const readableStatus = getReadableTicketStatus(ticket);
    const matchType =
      filterType === 'All' || ticket.custom_fields?.cf_complaint_type === filterType;
    const matchStatus = filterStatus === 'All' || readableStatus === filterStatus;
    return matchType && matchStatus;
  });

  const cleanDescription = (text = '') => {
    if (!text) return '';
    let cleaned = text.trim();
    cleaned = cleaned.replace(/^(dear\s+)?(respected\s+)?sir\/?madam[:,]?\s*/i, '');
    cleaned = cleaned.replace(/^(dear\s+)?(respected\s+)?sir[:,]?\s*/i, '');
    const upiMatch = cleaned.toLowerCase().indexOf('upi');
    if (upiMatch !== -1) cleaned = cleaned.substring(0, upiMatch);
    cleaned = cleaned.replace(/CENTRAL BANK OF INDIA[\s\S]*$/i, '');
    cleaned = cleaned.replace(/The information contained[\s\S]*$/i, '');
    cleaned = cleaned
      .replace(/\r?\n|\r/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    cleaned = cleaned.replace(/(,|and)\s*$/, '').trim();
    return cleaned;
  };

  function openSupportEmail() {
    const email = APP_CONSTANTS?.CBI_SUPPORT_EMAIL;
    // const email =  APP_CONSTANTS?.CBI_SUPPORT_EMAIL;

    const subject = 'Customer Support Request';

    // Detect platform
    const ua = navigator.userAgent.toLowerCase();

    if (/android/.test(ua)) {
      const intentUrl = `intent://compose?to=${email}&subject=${encodeURIComponent(
        subject
      )}#Intent;scheme=mailto;package=com.google.android.gm;end`;
      window.location.href = intentUrl;
      return;
    }

    if (/iphone|ipad|ipod/.test(ua)) {
      const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(subject)}`;
      window.location.href = mailtoUrl;
      return;
    }

    // Fallback for web (desktop)
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${encodeURIComponent(
      subject
    )}`;
    window.open(gmailUrl, '_blank');
  }

  return (
    <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center', fontFamily: 'Montserrat' }}>
        {capitalizeFirstLetter(selectedStatus)} Tickets
      </DialogTitle>

      <DialogContent dividers>
        <Stack
          sx={{ mb: 2 }}
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={2}
        >
          <Link
            component="button"
            underline="none"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
            onClick={() => openSupportEmail()}
          >
            <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {APP_CONSTANTS?.CBI_SUPPORT_EMAIL}
          </Link>

          <Typography sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <PhoneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {APP_CONSTANTS?.CBI_SUPPORT_CALL}
          </Typography>
        </Stack>

        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                <TableCell />
                <TableCell sx={{ fontWeight: 'bold' }}>Ticket-ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Complaint Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created At</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Updated At</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredTickets.length > 0 ? (
                filteredTickets.map((ticket) => {
                  const readableStatus = getReadableTicketStatus(ticket);
                  const isExpanded = expandedTicket === ticket.id;

                  return (
                    <React.Fragment key={ticket.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            onClick={() => {
                              if (isExpanded) {
                                // Collapse the currently expanded ticket
                                setExpandedTicket(null);
                                setConversations({});
                              } else {
                                // Expand new ticket
                                setExpandedTicket(ticket?.id);
                                setConversations({}); // clear previous conversations
                                fetchTicketData(ticket?.id); // fetch new ticket's conversations
                              }
                            }}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{ticket.freshdeskTicketId}</TableCell>
                        <TableCell>{ticket.custom_fields?.cf_complaint_type}</TableCell>
                        <TableCell>{cleanDescription(ticket.description_text)}</TableCell>
                        <TableCell>{isoDateFormatDmyhs(ticket.created_at)}</TableCell>
                        <TableCell>{isoDateFormatDmyhs(ticket.updated_at)}</TableCell>
                        <TableCell>
                          <Chip
                            label={readableStatus}
                            color={statusColor(readableStatus)}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                margin: 2,
                                bgcolor: '#f8f9fa',
                                borderRadius: 2,
                                p: 1,
                                maxHeight: 300, // set your fixed height
                                overflowY: 'auto' // scroll if content exceeds height
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Mail Chain / Comments
                              </Typography>
                              {conversations[ticket.id]?.length > 0 ? (
                                conversations[ticket.id].map((msg) => (
                                  <Box
                                    key={msg.id}
                                    sx={{
                                      borderBottom: '1px solid #eee',
                                      mb: 1,
                                      pb: 1
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ whiteSpace: 'pre-wrap' }}
                                      dangerouslySetInnerHTML={{
                                        __html: cleanDescription(msg.body_text) || msg.body
                                      }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                      {isoDateFormatDmyhs(msg.created_at)}
                                    </Typography>
                                  </Box>
                                ))
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No comments found.
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No tickets found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
}
